import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { FormField } from '@/components/auth/FormField';
import { SubmitButton } from '@/components/auth/SubmitButton';
import { AuthError } from '@/components/auth/AuthError';
import { AuthSuccess } from '@/components/auth/AuthSuccess';
import { supabase } from '@/lib/supabase';
import { describeAuthError } from '@/lib/authErrors';
import { validateEmail } from '@/lib/validation';

export default function ForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitError(null);
    const err = validateEmail(email);
    if (err) {
      setEmailError(err);
      return;
    }
    setEmailError(null);
    setLoading(true);

    try {
      const trimmed = email.trim();
      const { error } = await supabase.auth.resetPasswordForEmail(trimmed, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        setSubmitError(describeAuthError(error));
        return;
      }
      setSubmittedEmail(trimmed);
    } catch (err) {
      setSubmitError(describeAuthError(err));
    } finally {
      setLoading(false);
    }
  }

  if (submittedEmail) {
    return (
      <AuthLayout
        title="Check your inbox"
        footer={
          <>
            Remembered it?{' '}
            <Link to="/login" className="text-ink underline hover:text-candle-dark">
              Sign in
            </Link>
          </>
        }
      >
        <AuthSuccess>
          If an account exists for <strong className="font-semibold">{submittedEmail}</strong>, we
          sent a password reset link. Open it to choose a new password.
        </AuthSuccess>
        <p className="text-sm text-ink-mid leading-relaxed">
          The link expires after one hour. If you do not see the email, check your spam folder.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Forgot password"
      subtitle="We'll send a reset link to your email."
      footer={
        <>
          <Link to="/login" className="text-ink underline hover:text-candle-dark">
            Back to sign in
          </Link>
        </>
      }
    >
      <AuthError message={submitError} />

      <form onSubmit={handleSubmit} noValidate>
        <FormField
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={emailError}
          required
        />

        <SubmitButton loading={loading}>{loading ? 'Sending…' : 'Send reset link'}</SubmitButton>
      </form>
    </AuthLayout>
  );
}
